import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Edit, Phone, Mail, Globe } from "lucide-react";
import {
  getBusinessDetails,
  updateBusinessDetails,
  updateBusinessGallery,
  resetUpdateStatus,
  fetchServicesBySlug,
  fetchBusinessOwnerPaymentModes
} from "../../store/slices/businessOwnerSlice";
import {
  BusinessDetailsEditForm,
  BusinessOwnerGalleryEditForm,
  
} from "../../components/BusinessOwnerEditForms";
import ReusableModal, { MODAL_TYPES } from "../../components/ReusableModal";
import { showApiError, showApiSuccess } from "../../utils/toast";
import Loader from "../../components/Loader";

export default function BusinessSettings() {
  const dispatch = useDispatch();
  const { business, loading, updateStatus, updateError, services, paymentModes = [] } = useSelector(
    (state) => state.businessOwner
  );

  const [modalState, setModalState] = useState({
    open: false,
    type: null,
    section: null,
    loading: false,
  });

  useEffect(() => {
    dispatch(getBusinessDetails());
    dispatch(fetchBusinessOwnerPaymentModes());
  }, [dispatch]);

  useEffect(() => {
    if (updateStatus === "success") {
      showApiSuccess("Business updated successfully");
      setModalState({ open: false, type: null, section: null, loading: false });
      dispatch(resetUpdateStatus());
      dispatch(getBusinessDetails());
    } else if (updateStatus === "failed") {
      showApiError(null, updateError);
      setModalState((prev) => ({ ...prev, loading: false }));
      dispatch(resetUpdateStatus());
    }
  }, [updateStatus, updateError, dispatch]);

  const openModal = (section) => {
    setModalState({
      open: true,
      type: MODAL_TYPES.CUSTOM,
      section,
      loading: false,
    });
  };

  const closeModal = () => {
    setModalState({
      open: false,
      type: null,
      section: null,
      loading: false,
    });
  };

  const handleSaveBusinessDetails = async (data) => {
    setModalState((prev) => ({ ...prev, loading: true }));
    await dispatch(updateBusinessDetails(data));
  };



  const handleSaveGallery = async (data) => {
    setModalState((prev) => ({ ...prev, loading: true }));
    await dispatch(updateBusinessGallery(data));
  };

  if (loading && !business) {
    return <Loader />;
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Business Settings</h2>
            <p className="text-gray-600 mt-1">
              Manage your business profile and settings
            </p>
          </div>
        </div>

        {business ? (
          <div className="space-y-6">
            {/* Business Details Card - Maps to PUT /business */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold text-gray-800">
                  Business Details
                </h3>
                <button
                  onClick={() => openModal("business_details")}
                  className="text-blue-600 hover:text-blue-800 p-2 rounded hover:bg-blue-50"
                  title="Edit Business Details"
                >
                  <Edit className="h-4 w-4" />
                </button>
              </div>
              
              {/* Basic Information */}
              <div className="mb-4">
                <h4 className="font-medium text-gray-700 mb-2">Basic Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Business Name</p>
                    <p className="font-medium">{business.business_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">About</p>
                    <p className="font-medium">
                      {business.about_business || "Not provided"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="mb-4">
                <h4 className="font-medium text-gray-700 mb-2">Contact Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 flex items-center gap-1">
                      <Phone className="h-4 w-4" /> Contact Numbers
                    </p>
                    <p className="font-medium">
                      {business.contact_numbers?.join(", ") || "Not provided"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 flex items-center gap-1">
                      <Phone className="h-4 w-4" /> WhatsApp
                    </p>
                    <p className="font-medium">
                      {business.whatsapp_number || "Not provided"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 flex items-center gap-1">
                      <Mail className="h-4 w-4" /> Email
                    </p>
                    <p className="font-medium">{business.email || "Not provided"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 flex items-center gap-1">
                      <Globe className="h-4 w-4" /> Website
                    </p>
                    <p className="font-medium">{business.website || "Not provided"}</p>
                  </div>
                </div>
              </div>

              {/* Location Information */}
              <div className="mb-4">
                <h4 className="font-medium text-gray-700 mb-2">Location</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Address</p>
                    <p className="font-medium">{business.address || "Not provided"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Pincode</p>
                    <p className="font-medium">{business.pincode || "Not provided"}</p>
                  </div>
                </div>
              </div>

              {/* Payment Modes */}
              <div className="mb-4">
                <h4 className="font-medium text-gray-700 mb-2">Payment Modes</h4>
                <div className="flex flex-wrap gap-2">
                  {business.payment_modes && business.payment_modes.length > 0 ? (
                    business.payment_modes.map((mode, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                      >
                        {mode}
                      </span>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm">No payment modes set</p>
                  )}
                </div>
              </div>

              {/* Services */}
              <div className="mb-4">
                <h4 className="font-medium text-gray-700 mb-2">Services</h4>
                <div className="flex flex-wrap gap-2">
                  {business.services && business.services.length > 0 ? (
                    business.services.map((service) => (
                      <span
                        key={service.uuid}
                        className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                      >
                        {service.name}
                      </span>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm">No services set</p>
                  )}
                </div>
              </div>

              {/* Business Timings */}
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Business Timings</h4>
                {business.business_timings &&
                Object.keys(business.business_timings).length > 0 ? (
                  <div className="space-y-2">
                    {Object.entries(business.business_timings).map(
                      ([day, slots]) => (
                        <div key={day} className="flex justify-between">
                          <span className="font-medium capitalize">{day}</span>
                          <span className="text-gray-600">
                            {slots && slots.length > 0
                              ? slots
                                  .map((slot) => `${slot.open} - ${slot.close}`)
                                  .join(", ")
                              : "Closed"}
                          </span>
                        </div>
                      )
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No timings set</p>
                )}
              </div>
            </div>



            {/* Gallery Card - Maps to POST /business/gallery */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold text-gray-800">Gallery</h3>
                <button
                  onClick={() => openModal("gallery")}
                  className="text-blue-600 hover:text-blue-800 p-2 rounded hover:bg-blue-50"
                  title="Edit Gallery"
                >
                  <Edit className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {business.gallery_images && business.gallery_images.length > 0 ? (
                  business.gallery_images.map((img) => (
                    <div key={img.uuid} className="aspect-square">
                      <img
                        src={img.image}
                        alt="Gallery"
                        className="w-full h-full object-cover rounded-lg border"
                      />
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm col-span-full">
                    No images uploaded
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500">No business details found</p>
          </div>
        )}
      </div>

      {/* Edit Modals */}
      <ReusableModal
        open={modalState.open}
        onOpenChange={closeModal}
        type={modalState.type}
        data={business}
        loading={modalState.loading}
        showActions={false}
        size="large"
      >
        {modalState.section === "business_details" && (
          <BusinessDetailsEditForm
            data={business}
            onSave={handleSaveBusinessDetails}
            loading={modalState.loading}
            paymentModes={paymentModes}
          />
        )}

        {modalState.section === "gallery" && (
          <BusinessOwnerGalleryEditForm
            data={business}
            onSave={handleSaveGallery}
            loading={modalState.loading}
          />
        )}
      </ReusableModal>
    </div>
  );
}
